import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ArrowRightIcon, SendIcon, MessageCircleIcon, CheckIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getUTMDataForSubmission, appendUTMToUrl } from '../utils/utm';
interface ContactFormPopupProps {
  isOpen: boolean;
  onClose: () => void;
}
const WORKER_URL = 'https://white-bonus-e94a.maxkhmlyt.workers.dev';
// Контактные данные
const TELEGRAM_LINK = 'https://t.me/+380508399309';
const WHATSAPP_LINK = 'https://api.whatsapp.com/send/?phone=380508399309&text&type=phone_number&app_absent=0';
// Country codes sorted by length (longest first) for proper matching
const COUNTRY_CODES = [{
  code: '+380',
  flag: '🇺🇦',
  country: 'Ukraine'
}, {
  code: '+375',
  flag: '🇧🇾',
  country: 'Belarus'
}, {
  code: '+372',
  flag: '🇪🇪',
  country: 'Estonia'
}, {
  code: '+371',
  flag: '🇱🇻',
  country: 'Latvia'
}, {
  code: '+370',
  flag: '🇱🇹',
  country: 'Lithuania'
}, {
  code: '+421',
  flag: '🇸🇰',
  country: 'Slovakia'
}, {
  code: '+420',
  flag: '🇨🇿',
  country: 'Czech'
}, {
  code: '+386',
  flag: '🇸🇮',
  country: 'Slovenia'
}, {
  code: '+385',
  flag: '🇭🇷',
  country: 'Croatia'
}, {
  code: '+381',
  flag: '🇷🇸',
  country: 'Serbia'
}, {
  code: '+359',
  flag: '🇧🇬',
  country: 'Bulgaria'
}, {
  code: '+358',
  flag: '🇫🇮',
  country: 'Finland'
}, {
  code: '+357',
  flag: '🇨🇾',
  country: 'Cyprus'
}, {
  code: '+356',
  flag: '🇲🇹',
  country: 'Malta'
}, {
  code: '+353',
  flag: '🇮🇪',
  country: 'Ireland'
}, {
  code: '+352',
  flag: '🇱🇺',
  country: 'Luxembourg'
}, {
  code: '+351',
  flag: '🇵🇹',
  country: 'Portugal'
}, {
  code: '+350',
  flag: '🇬🇮',
  country: 'Gibraltar'
}, {
  code: '+972',
  flag: '🇮🇱',
  country: 'Israel'
}, {
  code: '+971',
  flag: '🇦🇪',
  country: 'UAE'
}, {
  code: '+966',
  flag: '🇸🇦',
  country: 'Saudi Arabia'
}, {
  code: '+994',
  flag: '🇦🇿',
  country: 'Azerbaijan'
}, {
  code: '+995',
  flag: '🇬🇪',
  country: 'Georgia'
}, {
  code: '+998',
  flag: '🇺🇿',
  country: 'Uzbekistan'
}, {
  code: '+48',
  flag: '🇵🇱',
  country: 'Poland'
}, {
  code: '+49',
  flag: '🇩🇪',
  country: 'Germany'
}, {
  code: '+44',
  flag: '🇬🇧',
  country: 'UK'
}, {
  code: '+43',
  flag: '🇦🇹',
  country: 'Austria'
}, {
  code: '+41',
  flag: '🇨🇭',
  country: 'Switzerland'
}, {
  code: '+40',
  flag: '🇷🇴',
  country: 'Romania'
}, {
  code: '+39',
  flag: '🇮🇹',
  country: 'Italy'
}, {
  code: '+36',
  flag: '🇭🇺',
  country: 'Hungary'
}, {
  code: '+34',
  flag: '🇪🇸',
  country: 'Spain'
}, {
  code: '+33',
  flag: '🇫🇷',
  country: 'France'
}, {
  code: '+32',
  flag: '🇧🇪',
  country: 'Belgium'
}, {
  code: '+31',
  flag: '🇳🇱',
  country: 'Netherlands'
}, {
  code: '+30',
  flag: '🇬🇷',
  country: 'Greece'
}, {
  code: '+47',
  flag: '🇳🇴',
  country: 'Norway'
}, {
  code: '+46',
  flag: '🇸🇪',
  country: 'Sweden'
}, {
  code: '+45',
  flag: '🇩🇰',
  country: 'Denmark'
}, {
  code: '+90',
  flag: '🇹🇷',
  country: 'Turkey'
}, {
  code: '+1',
  flag: '🇺🇸',
  country: 'USA/Canada'
}, {
  code: '+7',
  flag: '🇷🇺',
  country: 'Russia/Kazakhstan'
}, {
  code: '+61',
  flag: '🇦🇺',
  country: 'Australia'
}, {
  code: '+81',
  flag: '🇯🇵',
  country: 'Japan'
}, {
  code: '+82',
  flag: '🇰🇷',
  country: 'South Korea'
}, {
  code: '+86',
  flag: '🇨🇳',
  country: 'China'
}, {
  code: '+91',
  flag: '🇮🇳',
  country: 'India'
}, {
  code: '+55',
  flag: '🇧🇷',
  country: 'Brazil'
}, {
  code: '+52',
  flag: '🇲🇽',
  country: 'Mexico'
}].sort((a, b) => b.code.length - a.code.length);
export function ContactFormPopup({
  isOpen,
  onClose
}: ContactFormPopupProps) {
  const {
    t,
    language,
    getPath
  } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const languageNames: Record<string, string> = {
    en: 'English',
    ru: 'Русский',
    ua: 'Українська'
  };
  const consentText: Record<string, string> = {
    pl: 'Wyrażam zgodę na przetwarzanie moich danych osobowych',
    en: 'I agree to the processing of my personal data',
    ru: 'Я согласен на обработку моих персональных данных',
    ua: 'Я погоджуюсь на обробку моїх персональних даних'
  };
  const consentErrorText: Record<string, string> = {
    en: 'Please agree to continue',
    ru: 'Пожалуйста, дайте согласие для продолжения',
    ua: 'Будь ласка, дайте згоду для продовження'
  };
  const detectedCountry = useMemo(() => {
    const phone = formData.phone.replace(/\s/g, '');
    if (!phone.startsWith('+')) return null;
    for (const country of COUNTRY_CODES) {
      if (phone.startsWith(country.code)) {
        return country;
      }
    }
    return null;
  }, [formData.phone]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleConsentChange = () => {
    setConsent(!consent);
    if (!consent) {
      setConsentError(false);
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!consent) {
      setConsentError(true);
      return;
    }
    setIsSubmitting(true);
    const utmData = getUTMDataForSubmission();
    try {
      await fetch(WORKER_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message || 'Не указано',
          form_type: window.location.pathname.includes('instagram_ads') ? 'Instagram Ads (Popup)' : window.location.pathname.includes('course') ? 'Курс (Popup)' : 'Консультация (Popup)',
          page_url: window.location.href,
          language: languageNames[language] || language,
          ...utmData
        })
      });
    } catch {
      // Ignore errors
    } // Fire Meta Pixel Lead event immediately before redirect
    try {
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('track', 'Lead');
      }
    } catch (e) {}
    // Small delay to ensure the pixel request goes through before the page unloads
    setTimeout(() => {
      const thankYouPath = getPath('thank_you_page');
      // Используем pushState вместо location.href, чтобы React переключил страницу без перезагрузки (обходит ошибку 404 сервера)
      window.history.pushState({}, '', appendUTMToUrl(thankYouPath));
      window.dispatchEvent(new Event('popstate'));
      window.scrollTo(0, 0);
      onClose();
    }, 300);
  };
  return <AnimatePresence data-id="element-76">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm" data-id="element-77" />

          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[101] flex items-center justify-center p-4" data-id="element-78">
            <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl border p-6 sm:p-8" style={{
          borderColor: 'var(--border)',
          backgroundColor: 'var(--bg-secondary)'
        }} data-id="element-79">
              <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-lg transition-colors hover:bg-black/10" style={{
            color: 'var(--text-muted)'
          }} data-id="element-80">
                <XIcon className="w-5 h-5" data-id="element-81" />
              </button>

              <div className="mb-6 text-center pr-8" data-id="element-82">
                <h3 className="text-2xl font-bold mb-2" style={{
              color: 'var(--text-primary)'
            }} data-id="element-83">
                  {t('consultation.title')}{' '}
                  <span style={{
                color: 'var(--accent)'
              }} data-id="element-84">
                    {t('consultation.titleHighlight')}
                  </span>
                </h3>
                <p className="text-sm" style={{
              color: 'var(--text-secondary)'
            }} data-id="element-85">
                  {t('consultation.subtitle')}
                </p>
              </div>

              <div className="flex gap-3 mb-6" data-id="element-86">
                <motion.a href={TELEGRAM_LINK} target="_blank" rel="noopener noreferrer" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="flex flex-1 items-center justify-center gap-2 rounded-xl py-3 font-semibold transition-all border text-sm" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)'
            }} data-id="element-87">
                  <SendIcon className="h-4 w-4" style={{
                color: 'var(--accent)'
              }} data-id="element-88" />

                  {t('consultation.telegram')}
                </motion.a>

                <motion.a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="flex flex-1 items-center justify-center gap-2 rounded-xl py-3 font-semibold transition-all border text-sm" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)'
            }} data-id="element-89">
                  <MessageCircleIcon className="h-4 w-4" style={{
                color: 'var(--accent)'
              }} data-id="element-90" />

                  {t('consultation.whatsapp')}
                </motion.a>
              </div>

              <div className="my-6 flex items-center gap-4" data-id="element-91">
                <div className="h-px flex-1" style={{
              backgroundColor: 'var(--border)'
            }} data-id="element-92" />

                <span className="text-xs" style={{
              color: 'var(--text-muted)'
            }} data-id="element-93">
                  {t('consultation.or')}
                </span>
                <div className="h-px flex-1" style={{
              backgroundColor: 'var(--border)'
            }} data-id="element-94" />
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" data-id="element-95">
                <div data-id="element-96">
                  <label htmlFor="popup-name" className="mb-2 block text-sm font-medium" style={{
                color: 'var(--text-secondary)'
              }} data-id="element-97">
                    {t('consultation.name')} *
                  </label>
                  <input type="text" id="popup-name" name="name" value={formData.name} onChange={handleChange} required className="w-full rounded-xl border px-4 py-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-neutral-500/30" style={{
                borderColor: 'var(--border)',
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)'
              }} placeholder={t('consultation.namePlaceholder')} data-id="element-98" />
                </div>

                <div data-id="element-99">
                  <label htmlFor="popup-email" className="mb-2 block text-sm font-medium" style={{
                color: 'var(--text-secondary)'
              }} data-id="element-100">
                    {t('consultation.email')} *
                  </label>
                  <input type="email" id="popup-email" name="email" value={formData.email} onChange={handleChange} required className="w-full rounded-xl border px-4 py-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-neutral-500/30" style={{
                borderColor: 'var(--border)',
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)'
              }} placeholder={t('consultation.emailPlaceholder')} data-id="element-101" />
                </div>

                <div data-id="element-102">
                  <label htmlFor="popup-phone" className="mb-2 block text-sm font-medium" style={{
                color: 'var(--text-secondary)'
              }} data-id="element-103">
                    {t('consultation.phone')} *
                  </label>
                  <div className="relative" data-id="element-104">
                    {detectedCountry && <div className="absolute left-3 top-1/2 -translate-y-1/2 text-xl pointer-events-none" title={detectedCountry.country} data-id="element-105">
                        {detectedCountry.flag}
                      </div>}
                    <input type="tel" id="popup-phone" name="phone" value={formData.phone} onChange={handleChange} required className="w-full rounded-xl border px-4 py-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-neutral-500/30" style={{
                  borderColor: 'var(--border)',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  paddingLeft: detectedCountry ? '2.75rem' : '1rem'
                }} placeholder="+1 234 567 8900" data-id="element-106" />
                  </div>
                  <p className="mt-1 text-xs" style={{
                color: 'var(--text-muted)'
              }} data-id="element-107">
                    {language === 'ru' ? 'Введите номер с кодом страны' : language === 'ua' ? 'Введіть номер з кодом країни' : 'Enter number with country code'}
                  </p>
                </div>

                <div data-id="element-108">
                  <label htmlFor="popup-message" className="mb-2 block text-sm font-medium" style={{
                color: 'var(--text-secondary)'
              }} data-id="element-109">
                    {t('consultation.desc')}
                  </label>
                  <textarea id="popup-message" name="message" value={formData.message} onChange={handleChange} rows={3} className="w-full resize-none rounded-xl border px-4 py-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-neutral-500/30" style={{
                borderColor: 'var(--border)',
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)'
              }} placeholder={t('consultation.descPlaceholder')} data-id="element-110" />
                </div>

                <motion.div className={`flex items-start gap-3 p-3 rounded-xl transition-all ${consentError ? 'bg-red-500/10' : ''}`} animate={consentError ? {
              x: [0, -5, 5, -5, 5, 0]
            } : {}} transition={{
              duration: 0.4
            }} data-id="element-111">
                  <button type="button" onClick={handleConsentChange} className="flex-shrink-0 w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center transition-all" style={{
                borderColor: consentError ? '#ef4444' : consent ? 'var(--accent)' : 'var(--border)',
                backgroundColor: consent ? 'var(--accent)' : 'transparent'
              }} data-id="element-112">
                    {consent && <CheckIcon className="w-3 h-3 text-white" data-id="element-113" />}
                  </button>
                  <div className="flex-1" data-id="element-114">
                    <label className="text-xs cursor-pointer" style={{
                  color: consentError ? '#ef4444' : 'var(--text-secondary)'
                }} onClick={handleConsentChange} data-id="element-115">
                      {consentText[language] || consentText.en} *
                    </label>
                    {consentError && <motion.p initial={{
                  opacity: 0,
                  y: -5
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="text-xs mt-1 font-medium" style={{
                  color: '#ef4444'
                }} data-id="element-116">
                        {consentErrorText[language] || consentErrorText.en}
                      </motion.p>}
                  </div>
                </motion.div>

                <motion.button type="submit" disabled={isSubmitting} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="group w-full rounded-xl py-3.5 font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-70 text-sm" style={{
              backgroundColor: 'var(--accent)',
              color: '#FFFFFF'
            }} data-id="element-117">
                  <span className="flex items-center justify-center gap-2" data-id="element-118">
                    {isSubmitting ? t('consultation.sending') : <>
                        {t('consultation.cta')}
                        <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-1" data-id="element-119" />
                      </>}
                  </span>
                </motion.button>
              </form>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}